import {
  ExternalLink,
  Globe,
  Mail,
  MessagesSquare,
  Phone,
  ShoppingBag,
  ThumbsUp,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '../../l10n/i18n';
import { useToast } from '../core/widgets/toasts';

// Links as the mobile app's links.dart draws them: whole rows that look like links, the address under
// the name, the arrow out. Only web and mail addresses ever become links.

/** A web or mail address that may become a link, or nothing. */
export function safeUrl(url: string | undefined): URL | undefined {
  if (!url) return undefined;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' || u.protocol === 'mailto:' ? u : undefined;
  } catch {
    return undefined;
  }
}

const isMail = (u: URL) => u.protocol === 'mailto:';

function decoded(s: string): string {
  try {
    return decodeURI(s);
  } catch {
    return s;
  }
}

const bareHost = (u: URL) => (u.hostname.startsWith('www.') ? u.hostname.slice(4) : u.hostname);

/** An address as a person reads it: no scheme, no "www.", no trailing slash; an email for `mailto:`. */
export function prettyUrl(u: URL): string {
  if (isMail(u)) return decoded(u.pathname);
  const path = u.pathname.endsWith('/') ? u.pathname.slice(0, -1) : u.pathname;
  return decoded(`${bareHost(u)}${path}`);
}

/** [prettyUrl] short enough for one line: a long path keeps its end, which tells the pages apart. */
export function shortUrl(u: URL): string {
  const pretty = prettyUrl(u);
  const segments = u.pathname.split('/').filter(Boolean);
  if (pretty.length <= 36 || segments.length < 3 || isMail(u)) return pretty;
  return decoded(`${bareHost(u)}/…/${segments.slice(-2).join('/')}`);
}

/** What a link leads to, as an icon: a Facebook group, a page, an email, a store listing, a site. */
export function linkIcon(u: URL): LucideIcon {
  const host = u.hostname.toLowerCase();
  if (isMail(u)) return Mail;
  if (u.protocol === 'tel:') return Phone;
  if (host.endsWith('facebook.com') && u.pathname.startsWith('/groups/')) return Users;
  if (host.endsWith('facebook.com')) return ThumbsUp;
  if (host.endsWith('reddit.com')) return MessagesSquare;
  if (host === 'play.google.com' || host === 'apps.apple.com') return ShoppingBag;
  return Globe;
}

/** Props that open an address outside the app: a new tab for the web, the mail app for `mailto:`. */
export const outside = (u: URL) =>
  isMail(u) ? { href: u.href } : { href: u.href, target: '_blank', rel: 'noopener noreferrer' };

/** Copies text and says so: `message`, or "Nukopijuota". */
export function useCopy() {
  const t = useT();
  const toast = useToast();
  return (text: string, message?: string) => {
    const done = message ?? t('copied');
    const failed = t('errorGeneric');
    const fallback = () => toast(copyBySelection(text) ? done : failed);
    if (!navigator.clipboard) return fallback();
    navigator.clipboard.writeText(text).then(() => toast(done), fallback);
  };
}

/** The old way to copy, for browsers that refuse the clipboard API (an unfocused window, for one). */
function copyBySelection(text: string): boolean {
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    area.remove();
  }
}

function TileIcon({ icon: Icon, link }: { icon: LucideIcon; link?: boolean }) {
  return (
    <span className={link ? 'iv-tile-icon link' : 'iv-tile-icon'}>
      <Icon size={20} aria-hidden />
    </span>
  );
}

/** A link that looks like one: a whole row to click, the address under its name, the arrow out.
 * `onOpen` runs as it opens, for example to copy the text the person will paste there. */
export function LinkTile({ label, url, icon, onOpen }: { label: string; url: URL; icon?: LucideIcon; onOpen?: () => void }) {
  const address = shortUrl(url);
  return (
    <a className="iv-tile iv-link-tile" {...outside(url)} onClick={onOpen} title={isMail(url) ? undefined : url.href}>
      <TileIcon icon={icon ?? linkIcon(url)} link />
      <span className="iv-tile-text">
        <span className="title-small iv-clamp">{label}</span>
        {address && address !== label && <span className="body-small iv-tile-address iv-clamp">{address}</span>}
      </span>
      <ExternalLink size={20} className="iv-tile-out" aria-hidden />
    </a>
  );
}

/** A place or a person shown for information only: the same shape as a LinkTile, nothing to click. */
export function PlaceTile({ label, detail, icon }: { label: string; detail?: string; icon: LucideIcon }) {
  return (
    <div className="iv-tile">
      <TileIcon icon={icon} />
      <span className="iv-tile-text">
        <span className="title-small">{label}</span>
        {detail && <span className="body-small">{detail}</span>}
      </span>
    </div>
  );
}

/** Tiles one under another. */
export function TileList({ children }: { children: ReactNode }) {
  return <div className="iv-tiles">{children}</div>;
}

const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|https?:\/\/[^\s<>"]+/g;
const short = (s: string) => (s.length <= 42 ? s : `${s.slice(0, 40)}…`);

/** Text from an employee with its links made clickable: bare web addresses and `[label](url)`.
 * `**bold**` is shown bold. Everything else stays text; nothing is parsed as HTML. */
export function LinkedText({ text }: { text: string }) {
  const runs: ReactNode[] = [];
  let at = 0;
  let n = 0;
  for (const m of text.matchAll(pattern)) {
    const start = m.index;
    if (start > at) runs.push(text.slice(at, start));
    at = start + m[0].length;
    if (m[1] !== undefined) {
      runs.push(<strong key={n++}>{m[1]}</strong>);
    } else if (m[2] !== undefined) {
      const u = safeUrl(m[3]);
      runs.push(
        u ? (
          <a key={n++} className="iv-inline-link" {...outside(u)} title={u.href}>
            {m[2]}
          </a>
        ) : (
          m[0]
        ),
      );
    } else {
      // A sentence's full stop or closing bracket is not part of the address.
      let url = m[0];
      const tail = /[.,;:!?)\]]+$/.exec(url);
      if (tail) {
        url = url.slice(0, tail.index);
        at -= tail[0].length;
      }
      const u = safeUrl(url);
      runs.push(
        u ? (
          <a key={n++} className="iv-inline-link" {...outside(u)} title={u.href}>
            {short(prettyUrl(u))}
          </a>
        ) : (
          url
        ),
      );
    }
  }
  if (at < text.length) runs.push(text.slice(at));
  return <>{runs}</>;
}
